import { DestroyRef, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of, switchMap, take, tap } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AppRoutes,
  AvatarResult,
  CacheKey,
  CAMERA_TOKEN,
  CameraProvider,
  MODAL_TOKEN,
  ModalProvider,
  NAVIGATION_TOKEN,
  NavigationProvider,
  PresentModalComponent,
  TOAST_TOKEN,
  ToastProvider,
  USER_DATA_TOKEN,
  UserDataProvider,
  UserModel
} from '@core';
import {
  RemoveAccountCommandHandler,
  SetReferrerCommandHandler,
  UpdateUserCommandHandler
} from '../../../application/handlers';
import { getFirebaseAuthErrorMessage } from '@auth';

export interface Base64imageModel {
  readonly isAvailable: boolean;
  readonly base64: string;
}

@Injectable()
export class EditProfileViewService {
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);
  private readonly updateUserCommandHandler: UpdateUserCommandHandler = inject(UpdateUserCommandHandler);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly router: Router = inject(Router);
  private readonly removeAccountCommandHandler: RemoveAccountCommandHandler = inject(RemoveAccountCommandHandler);
  private readonly setReferrerCommandHandler: SetReferrerCommandHandler = inject(SetReferrerCommandHandler);
  private readonly cameraProvider: CameraProvider = inject(CAMERA_TOKEN);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly toastProvider: ToastProvider = inject(TOAST_TOKEN);
  private readonly navigationProvider: NavigationProvider = inject(NAVIGATION_TOKEN);

  readonly userData$: Observable<UserModel> = this.userDataProvider.userData$;

  readonly base64image: WritableSignal<Base64imageModel> = signal<Base64imageModel>({
    isAvailable: false,
    base64: ''
  });

  private readonly initialFormSnapshot: WritableSignal<Record<string, unknown> | null> = signal<Record<
    string,
    unknown
  > | null>(null);

  initializeForm(profileForm: FormGroup): void {
    this.userData$
      .pipe(
        take(1),
        tap((userData: UserModel) => {
          profileForm.patchValue({
            name: userData?.name,
            lastName: userData?.surname,
            phone: userData?.phone,
            preferredLevel: userData?.playerPreferences?.preferredLevel,
            preferredPosition: userData?.playerPreferences?.preferredPosition
          });
          this.initialFormSnapshot.set(profileForm.getRawValue());
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  hasUnsavedChanges(profileForm: FormGroup): boolean {
    const initial: Record<string, unknown> | null = this.initialFormSnapshot();
    if (!initial) return false;

    return JSON.stringify(profileForm.getRawValue()) !== JSON.stringify(initial);
  }

  saveData(profileForm: FormGroup): void {
    if (profileForm.valid) {
      this.updateUserCommandHandler
        .update({
          name: profileForm.get('name')?.value,
          surname: profileForm.get('lastName')?.value,
          phone: profileForm.get('phone')?.value,
          thumbnail: profileForm.get('avatar')?.value,
          preferredLevel: profileForm.get('preferredLevel')?.value,
          preferredPosition: profileForm.get('preferredPosition')?.value
        })
        .pipe(
          take(1),
          switchMap(() => {
            const referrerId: string = profileForm.get('referrerToken')?.value;
            if (!!referrerId) return this.setReferrerCommandHandler.setReferrer(referrerId);

            return of(void 0);
          }),
          take(1),
          tap(() => this.userDataProvider.setUserData())
        )
        .subscribe({
          next: () => {
            this.redirectAfterSuccess();

            // this.toastProvider.showToast({ message: 'Zapisano zmiany' });
          }
        });
    }
  }

  goToSettings(allowToCancel: boolean): void {
    if (!allowToCancel) return;

    this.navigationProvider.back();
  }

  deleteAccount(password?: string): void {
    this.removeAccountCommandHandler
      .removeAccount(password)
      .pipe(take(1))
      .subscribe({
        next: () => this.router.navigate([AppRoutes.AUTH]),
        error: (err: unknown) => this.showRemoveAccountError(err)
      });
  }

  private showRemoveAccountError(err: unknown): void {
    const { header, message } = getFirebaseAuthErrorMessage(err, 'Usuń konto');
    this.modalProvider.showModal$({
      component: PresentModalComponent,
      cssClass: 'present-modal',
      componentProps: { header, message, btnTxt: 'Zamknij' }
    });
  }

  async changeAvatar(profileForm: FormGroup): Promise<void> {
    const result: AvatarResult | null = await this.cameraProvider.pickAvatar();

    if (result) {
      this.base64image.set({
        isAvailable: true,
        base64: result.dataUrl
      });
      profileForm.patchValue({ avatar: result.dataUrl });
    }
  }

  cleanupSessionStorage(): void {
    sessionStorage.removeItem(CacheKey.RedirectAfterCompleteProfileUrl);
  }

  private redirectAfterSuccess(): void {
    const redirectAfterCompleteProfile: string | null = sessionStorage.getItem(
      CacheKey.RedirectAfterCompleteProfileUrl
    );

    if (!!redirectAfterCompleteProfile) {
      this.navigationProvider.replace(redirectAfterCompleteProfile);
      return;
    }

    this.userData$.pipe(take(1)).subscribe((user: UserModel) => {
      if (user) {
        this.navigationProvider.replace(`${AppRoutes.PROFILE}/${user.userId}`);
      }
    });
  }
}
